import { Icon } from "@pepperstone/pepper-ui-kit";
import clsx from "clsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  CashierTradingAccount,
  CashierTransactionView,
  EnumPaymentType,
} from "@/api/cashier";
import { ErrorPage } from "@/components/error-page/ErrorPage";
import { useCurrencies } from "@/hooks/deposit/useCurrencies";
import { useFormatAmount } from "@/hooks/useFormatAmount";
import { useFormatDate } from "@/hooks/useFormatDate";
import { EIcon } from "@/utils/enums";
import { TransactionHistoryPagination } from "./TransactionHistoryPagination";
import { TransactionHistoryPlatformDetails } from "./TransactionHistoryPlatformDetails";
import { TransactionHistorySavedPaymentDetails } from "./TransactionHistorySavedPaymentDetails";
import styles from "./TransactionHistoryTable.module.css";
import { TransactionHistoryTableLoadingSkeleton } from "./TransactionHistoryTableLoadingSkeleton";
import { TransactionHistoryTypeLabel } from "./TransactionHistoryTypeLabel";
import { TransactionStatusBadge } from "./TransactionStatusBadge";
import { moneyToMajorUnits } from "@/utils/helpers";

const defaultPageSize = 10;
const quickJumpStep = 5;

/**
  TransactionHistoryTable component for rendering a table of transaction history with pagination controls.
  @param data - Array of CashierTransactionView objects representing transaction data.
  @param pageSize - Number of transactions to display per page (default is 10).
  @param showRangeCounter - Boolean to show/hide the range counter (default is true).
  @param isLoading - Boolean to indicate if data is loading (default is false).
  @param isMobileView - Boolean to indicate if the view is mobile (default is false).
  @param tradingAccounts - Optional array of CashierTradingAccount objects representing trading accounts.
  @param onViewDetails - Optional callback function when "View Details" is clicked.
  @param handleErrorEvent - Optional callback function to handle error events.
  @returns A ReactElement representing the TransactionHistoryTable component.
*/
export const TransactionHistoryTable = ({
  data,
  pageSize = defaultPageSize,
  showRangeCounter = true,
  isLoading = false,
  isMobileView = false,
  tradingAccounts,
  onViewDetails,
  handleNoResultEvent,
}: {
  data: CashierTransactionView[];
  pageSize?: number;
  showRangeCounter?: boolean;
  isLoading: boolean;
  isMobileView: boolean;
  tradingAccounts: CashierTradingAccount[];
  onViewDetails?: (type: EnumPaymentType, referenceID: string) => void;
  handleNoResultEvent: () => void;
}) => {
  const { t } = useTranslation();
  const { formatDate } = useFormatDate();
  const { currencies } = useCurrencies();
  const { formatAmount } = useFormatAmount();

  const safePageSize = useMemo(
    () =>
      Number.isFinite(pageSize) && pageSize > 0
        ? Math.floor(pageSize)
        : defaultPageSize,
    [pageSize]
  );

  const total = useMemo(() => data.length, [data]);
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / safePageSize)),
    [total, safePageSize]
  );

  const [page, setPage] = useState(1);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const goTo = useCallback(
    (p: number) => {
      setPage((current) => {
        const target = Math.min(Math.max(p, 1), totalPages);
        return current === target ? current : target;
      });
    },
    [totalPages]
  );

  const pageSlice = useMemo(() => {
    if (total === 0) return [];
    const start = (page - 1) * safePageSize;
    const end = start + safePageSize;
    return data.slice(start, end);
  }, [data, page, safePageSize, total]);

  const paginationItems = useMemo(() => {
    const siblingCount = 1;
    const items: Array<number | "jump-back" | "jump-forward"> = [];

    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const leftSibling = Math.max(page - siblingCount, 2);
    const rightSibling = Math.min(page + siblingCount, totalPages - 1);

    items.push(1);

    if (leftSibling === 3) {
      items.push(2);
    } else if (leftSibling > 3) {
      items.push("jump-back");
    }

    for (let p = leftSibling; p <= rightSibling; p += 1) {
      items.push(p);
    }

    if (rightSibling === totalPages - 2) {
      items.push(totalPages - 1);
    } else if (rightSibling < totalPages - 2) {
      items.push("jump-forward");
    }

    items.push(totalPages);

    return items;
  }, [page, totalPages]);

  const startIndex = useMemo(
    () => (total === 0 ? 0 : (page - 1) * safePageSize + 1),
    [total, page, safePageSize]
  );
  const endIndex = useMemo(
    () => (total === 0 ? 0 : Math.min(page * safePageSize, total)),
    [total, page, safePageSize]
  );

  /* Helper to get display data for a specific transaction
    @param transaction - The CashierTransactionView object.
    @param isMobileView - A boolean indicating if the view is mobile or not.
    @returns An object containing formatted transaction amount, from account, and to account data.
  */
  const getTransactionDisplayData = (
    transaction: CashierTransactionView,
    isMobileView: boolean
  ) => {
    const transactionAmount = formatAmount(
      moneyToMajorUnits(transaction.money),
      currencies?.[transaction.money.code]
    );

    const fromTransfer =
      transaction.type === "transfer" && transaction.from_trading_account ? (
        <TransactionHistoryPlatformDetails
          tradingAccountId={transaction.from_trading_account}
          tradingAccounts={tradingAccounts}
        />
      ) : (
        <p className={isMobileView ? styles.adminAccountLabel : undefined}>
          {t("transactions.history.table.admin")}
        </p>
      );

    const toTransfer =
      transaction.type === "transfer" && transaction.to_trading_account ? (
        <TransactionHistoryPlatformDetails
          tradingAccountId={transaction.to_trading_account}
          tradingAccounts={tradingAccounts}
        />
      ) : null;

    const fromDeposit =
      transaction.type === "deposit" && transaction.saved_payment_account ? (
        <TransactionHistorySavedPaymentDetails
          savedPaymentAccountData={transaction.saved_payment_account}
        />
      ) : (
        "-"
      );

    //this probably should have been placed in the to field
    const toDeposit =
      transaction.type === "deposit" && transaction.to_trading_account ? (
        <TransactionHistoryPlatformDetails
          tradingAccountId={transaction.to_trading_account}
          tradingAccounts={tradingAccounts}
        />
      ) : null;

    const fromWithdrawal =
      transaction.type === "withdrawal" && transaction.from_trading_account ? (
        <TransactionHistoryPlatformDetails
          tradingAccountId={transaction.from_trading_account}
          tradingAccounts={tradingAccounts}
        />
      ) : null;

    const toWithdrawal =
      transaction.type === "withdrawal" && transaction.saved_payment_account ? (
        <TransactionHistorySavedPaymentDetails
          savedPaymentAccountData={transaction.saved_payment_account}
        />
      ) : (
        "-"
      );

    const fromCashAdjust =
      transaction.type === "cashAdjustment" ? (
        <p className={isMobileView ? styles.adminAccountLabel : undefined}>
          {t("transactions.history.table.admin")}
        </p>
      ) : null;
    const toCashAdjust =
      transaction.type === "cashAdjustment" ? (
        <TransactionHistoryPlatformDetails
          tradingAccountId={transaction.to_trading_account}
          tradingAccounts={tradingAccounts}
        />
      ) : null;

    return {
      transactionAmount,
      from:
        {
          transfer: fromTransfer,
          deposit: fromDeposit,
          withdrawal: fromWithdrawal,
          cashAdjustment: fromCashAdjust,
        }[transaction.type] || "-",
      to:
        {
          transfer: toTransfer,
          deposit: toDeposit,
          withdrawal: toWithdrawal,
          cashAdjustment: toCashAdjust,
        }[transaction.type] || "-",
    };
  };

  const footer = (
    <div className={styles.footer}>
      {showRangeCounter && (
        <div
          data-testid="transaction-history-pagination"
          className={styles.counter}
        >
          {t("transactions.history.table.showing", {
            from: startIndex,
            to: endIndex,
            total: total,
          })}
        </div>
      )}
      {totalPages > 1 && (
        <nav className={styles.pager}>
          <TransactionHistoryPagination
            onClick={() => goTo(page - 1)}
            disabled={page === 1}
          >
            <Icon icon="arrow-left" size={12} />
          </TransactionHistoryPagination>
          {paginationItems.map((item, index) => {
            if (item === "jump-back") {
              return (
                <TransactionHistoryPagination
                  key={`jump-back-${index}`}
                  aria-label="quick jump backward"
                  className={clsx(styles.pageNumber, styles.quickJump)}
                  onClick={() => goTo(page - quickJumpStep)}
                >
                  ...
                </TransactionHistoryPagination>
              );
            }

            if (item === "jump-forward") {
              return (
                <TransactionHistoryPagination
                  key={`jump-forward-${index}`}
                  aria-label="quick jump forward"
                  className={clsx(styles.pageNumber, styles.quickJump)}
                  onClick={() => goTo(page + quickJumpStep)}
                >
                  ...
                </TransactionHistoryPagination>
              );
            }

            const active = item === page;

            return (
              <TransactionHistoryPagination
                key={item}
                disabled={active}
                className={styles.pageNumber}
                onClick={!active ? () => goTo(item) : undefined}
              >
                {item}
              </TransactionHistoryPagination>
            );
          })}
          <TransactionHistoryPagination
            onClick={() => goTo(page + 1)}
            disabled={page === totalPages}
          >
            <Icon icon="arrow-right" size={12} />
          </TransactionHistoryPagination>
        </nav>
      )}
    </div>
  );

  return (
    <>
      {isLoading ? (
        <TransactionHistoryTableLoadingSkeleton isMobileView={isMobileView} />
      ) : total === 0 ? (
        <ErrorPage
          showButton={true}
          icon={EIcon.NoResults}
          title={t("transactions.no_results.title")}
          message={t("transactions.no_results.text")}
          buttonLabel={t("transactions.no_results.button_text")}
          buttonAction={handleNoResultEvent}
        />
      ) : isMobileView ? (
        <>
          <div className={styles.mobileTransactionList}>
            {pageSlice.map((transaction) => {
              const { transactionAmount, from, to } = getTransactionDisplayData(
                transaction,
                isMobileView
              );
              return (
                <div
                  key={transaction.reference_id}
                  className={styles.transactionCard}
                  onClick={() =>
                    onViewDetails?.(transaction.type, transaction.reference_id)
                  }
                >
                  <div className={styles.transactionHead}>
                    <h2>{transaction.reference_id}</h2>
                    <p>{transactionAmount}</p>
                  </div>
                  <div className={styles.dateAndStatus}>
                    <p>{formatDate(transaction.created_at)}</p>
                    <span className={styles.statusBadge}>
                      <TransactionStatusBadge status={transaction.status} />
                    </span>
                  </div>
                  <div className={styles.transactionType}>
                    <p>
                      {t("transactions.history.transaction_mobile_view.type")}
                    </p>
                    <span className={styles.typeLabel}>
                      <TransactionHistoryTypeLabel type={transaction.type} />
                    </span>
                  </div>
                  <div className={styles.fromAccount}>
                    <p>
                      {t("transactions.history.transaction_mobile_view.from")}
                    </p>
                    <span className={styles.accountDetails}>{from}</span>
                  </div>
                  <div className={styles.toAccount}>
                    <p>
                      {t("transactions.history.transaction_mobile_view.to")}
                    </p>
                    <span className={styles.accountDetails}>{to}</span>
                  </div>
                </div>
              );
            })}
          </div>
          {footer}
        </>
      ) : (
        <>
          <div className={styles.tablesWrap}>
            <div className={styles.leftTableContainer}>
              <table className={clsx(styles.table, styles.leftTable)}>
                <thead>
                  <tr>
                    <th>{t("transactions.history.table.time")}</th>
                    <th>{t("transactions.history.table.type")}</th>
                    <th>{t("transactions.history.table.order_number")}</th>
                    <th>{t("transactions.history.table.from")}</th>
                    <th>{t("transactions.history.table.to")}</th>
                    <th>{t("transactions.history.table.amount")}</th>
                    <th>{t("transactions.history.table.status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {pageSlice.map((transaction) => {
                    const { transactionAmount, from, to } =
                      getTransactionDisplayData(transaction, isMobileView);
                    return (
                      <tr key={transaction.reference_id}>
                        <td>{formatDate(transaction.created_at)}</td>
                        <td>
                          <TransactionHistoryTypeLabel
                            type={transaction.type}
                          />
                        </td>
                        <td>{transaction.reference_id}</td>
                        <td>{from}</td>
                        <td>{to}</td>
                        <td>{transactionAmount}</td>
                        <td className={styles.status}>
                          <TransactionStatusBadge status={transaction.status} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className={styles.rightTableContainer}>
              <table className={clsx(styles.table, styles.rightTable)}>
                <thead>
                  <tr>
                    <th>{t("transactions.history.table.action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {pageSlice.map((transaction) => (
                    <tr key={transaction.reference_id}>
                      <td>
                        <button
                          type="button"
                          onClick={() =>
                            onViewDetails?.(
                              transaction.type,
                              transaction.reference_id
                            )
                          }
                          className={styles.viewDetailsButton}
                        >
                          {t("transactions.history.table.view_details")}
                          <Icon icon="arrow-right-narrow" size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {footer}
        </>
      )}
    </>
  );
};
